/**
 * TiTiler source-URL helpers for COG / raster layers.
 *
 * ## GeoConverter → gTwin → GeoViewer contract
 *
 * GeoConverter writes a ready-to-stream absolute URL on the scene item
 * endpoint (`endpoints.<key>.href`). GeoViewer must:
 *
 *   1. Always load from that href — never invent a file location.
 *   2. Use one code path for local and remote (gTwin stores whatever
 *      GeoConverter wrote — localhost, https S3, or CDN).
 *   3. Pass that href into TiTiler as `?url=` after the fetch-side
 *      rewrites below (Docker localhost + private-S3 → /vsis3/).
 *   4. Never rewrite remote URLs back to `localhost:3011/converter-output`.
 *
 * Allowed fetch-side rewrites (do not change what gTwin stores):
 *   - `localhost` / `127.0.0.1` converter URLs → Docker-reachable host
 *     (`host.docker.internal` by default).
 *   - Virtual-hosted / path-style AWS S3 HTTPS (or `s3://`) URLs →
 *     `/vsis3/bucket/key` so GDAL uses signed `/vsis3/` with container
 *     AWS creds (Option B). Plain `s3://` is unreliable in some TiTiler
 *     builds (falls through to unsigned HTTP and 403s). Public CDN HTTPS
 *     URLs are left unchanged (Option A).
 */

function rewriteHost(): string {
  const fromEnv =
    typeof import.meta !== 'undefined'
      ? (import.meta.env?.VITE_TITILER_COG_FETCH_HOST as string | undefined)
      : undefined;
  const host = (fromEnv || 'host.docker.internal').trim();
  return host.length > 0 ? host : 'host.docker.internal';
}

function toVsis3(bucket: string, key: string): string {
  const cleanKey = key.replace(/^\/+/, '');
  return `/vsis3/${bucket}/${cleanKey}`;
}

/**
 * Map AWS S3 HTTPS / `s3://` object URLs to `/vsis3/bucket/key` for
 * authenticated TiTiler fetches. Non-S3 HTTPS (CDN, public) is unchanged.
 */
export function rewriteS3HttpsToS3Uri(url: string): string {
  const trimmed = url.trim();

  const s3Uri = /^s3:\/\/([^/]+)\/(.+)$/i.exec(trimmed);
  if (s3Uri) {
    return toVsis3(s3Uri[1], s3Uri[2]);
  }

  if (trimmed.startsWith('/vsis3/')) {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return url;
    }
    const host = parsed.hostname.toLowerCase();
    const path = decodeURIComponent(parsed.pathname.replace(/^\/+/, ''));
    if (!path) return url;

    // Virtual-hosted: bucket.s3.<region>.amazonaws.com/key
    // or bucket.s3.amazonaws.com/key
    const virtual = /^(.+)\.s3(?:[.-]([a-z0-9-]+))?\.amazonaws\.com$/i.exec(host);
    if (virtual) {
      const bucket = virtual[1];
      if (bucket.toLowerCase() !== 's3') {
        return toVsis3(bucket, path);
      }
    }

    // Path-style: s3.<region>.amazonaws.com/bucket/key
    if (/^s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i.test(host)) {
      const slash = path.indexOf('/');
      if (slash <= 0) return url;
      const bucket = path.slice(0, slash);
      const key = path.slice(slash + 1);
      if (!bucket || !key) return url;
      return toVsis3(bucket, key);
    }

    return url;
  } catch {
    return url;
  }
}

/**
 * Prepare a gTwin endpoint href for TiTiler `?url=`:
 * local converter → Docker host; private AWS S3 → /vsis3/; else as-is.
 */
export function rewriteConverterFetchUrl(url: string): string {
  const asS3 = rewriteS3HttpsToS3Uri(url);
  if (asS3 !== url) {
    return asS3;
  }

  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'localhost' && parsed.hostname !== '127.0.0.1') {
      return url;
    }
    // Only rewrite converter-output URLs (port 3011 or path).
    if (parsed.port !== '3011' && !parsed.pathname.includes('/converter-output/')) {
      return url;
    }
    parsed.hostname = rewriteHost();
    return parsed.toString();
  } catch {
    return url.replace(
      /^http:\/\/(localhost|127\.0\.0\.1):3011\//,
      `http://${rewriteHost()}:3011/`,
    );
  }
}

/**
 * Rewrite the `url=` query parameter inside a TiTiler tile template so the
 * tile server can fetch the COG (Docker localhost rewrite and/or /vsis3/).
 */
export function normalizeTitilerSourceHost(tilesUrl: string): string {
  const match = /([?&]url=)([^&]+)/.exec(tilesUrl);
  if (!match || match.index === undefined) return tilesUrl;
  const [, prefix, encoded] = match;
  let decoded: string;
  try {
    decoded = decodeURIComponent(encoded);
  } catch {
    return tilesUrl;
  }
  const rewrittenUrl = rewriteConverterFetchUrl(decoded);
  if (rewrittenUrl === decoded) return tilesUrl;
  const rewritten = encodeURIComponent(rewrittenUrl);
  const start = match.index + prefix.length;
  const end = match.index + match[0].length;
  return `${tilesUrl.slice(0, start)}${rewritten}${tilesUrl.slice(end)}`;
}

/**
 * TiTiler TileJSON returns absolute tile templates like
 * `http://localhost:8000/cog/tiles/...`. In the browser those bypass the
 * Vite `/titiler` proxy (and can hit corporate tunnel failures). When
 * `VITE_TILES_URL` is a same-origin path (e.g. `/titiler`), rewrite back.
 * Also rewrite :8002 (geoviewer-titiler override) back to the same-origin proxy.
 */
export function toBrowserTitilerUrl(tilesUrl: string): string {
  const tilesBase = (
    typeof import.meta !== 'undefined'
      ? (import.meta.env?.VITE_TILES_URL as string | undefined)
      : undefined
  )?.replace(/\/+$/, '') || '/titiler';
  if (!tilesBase.startsWith('/')) return tilesUrl;
  return tilesUrl.replace(
    /^https?:\/\/(?:localhost|127\.0\.0\.1):800[02](?=\/)/,
    tilesBase,
  );
}

/**
 * Browser-fetched artifacts (PMTiles, GeoJSON, GLB, …) cannot call private
 * S3 HTTPS URLs (CORS + 403). Rewrite to same-origin `/s3-assets/<bucket>/<key>`
 * so Vite's signed proxy (Option B) can Range-GET the object.
 *
 * Public CDN hosts are left unchanged. COG/TiTiler paths use
 * {@link rewriteConverterFetchUrl} → `/vsis3/` instead — do not use this
 * helper for TiTiler `?url=` sources.
 */
export function rewriteBrowserAssetUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed || trimmed.startsWith('/s3-assets/') || trimmed.startsWith('/converter-output/')) {
    return trimmed;
  }

  try {
    // s3://bucket/key
    const s3Uri = /^s3:\/\/([^/]+)\/(.+)$/i.exec(trimmed);
    if (s3Uri) {
      return `/s3-assets/${encodeURIComponent(s3Uri[1])}/${s3Uri[2]
        .split('/')
        .map(encodeURIComponent)
        .join('/')}`;
    }

    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return url;
    const host = parsed.hostname.toLowerCase();
    const path = decodeURIComponent(parsed.pathname.replace(/^\/+/, ''));
    if (!path) return url;

    // Virtual-hosted: bucket.s3.<region>.amazonaws.com/key
    const virtual = /^(.+)\.s3(?:[.-]([a-z0-9-]+))?\.amazonaws\.com$/i.exec(host);
    if (virtual && virtual[1].toLowerCase() !== 's3') {
      return `/s3-assets/${encodeURIComponent(virtual[1])}/${path
        .split('/')
        .map(encodeURIComponent)
        .join('/')}`;
    }

    // Path-style: s3.<region>.amazonaws.com/bucket/key
    if (/^s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i.test(host)) {
      const slash = path.indexOf('/');
      if (slash <= 0) return url;
      const bucket = path.slice(0, slash);
      const key = path.slice(slash + 1);
      if (!bucket || !key) return url;
      return `/s3-assets/${encodeURIComponent(bucket)}/${key
        .split('/')
        .map(encodeURIComponent)
        .join('/')}`;
    }

    return url;
  } catch {
    return url;
  }
}

/**
 * TiTiler `tilejson.json` without `tile_format` emits extensionless tile
 * templates that default to **JPEG**. Float DEM COGs compressed as tiny JPEGs
 * paint as salt-and-pepper static on first load; remounts that hit a `.png`
 * template look correct (QGIS-like). Always request PNG from TileJSON.
 */
export function withTitilerTileJsonPngFormat(tilejsonUrl: string): string {
  if (/[?&]tile_format=/i.test(tilejsonUrl)) return tilejsonUrl;
  const sep = tilejsonUrl.includes('?') ? '&' : '?';
  return `${tilejsonUrl}${sep}tile_format=png`;
}

/**
 * Ensure an XYZ template ends with `{y}.png` (not bare `{y}` or `{y}.jpg`).
 * Leaves `.tif`/`.tiff` alone for ElevationLayer float meshes.
 */
export function ensureTitilerPngTileTemplate(tilesUrl: string): string {
  const match = tilesUrl.match(/^(.*?\/\{z\}\/\{x\}\/\{y\})(\.[A-Za-z0-9]+)?(\?.*)?$/);
  if (!match) return tilesUrl;
  const [, path, ext, query = ''] = match;
  if (ext && /^\.(tif|tiff)$/i.test(ext)) return tilesUrl;
  if (ext && /^\.png$/i.test(ext)) return tilesUrl;
  return `${path}.png${query}`;
}

/** Route a session source-CRS override to the CRS-aware raster service. */
export function withRasterSourceCrsOverride(tilesUrl: string, sourceCrs: string | null | undefined): string {
  const normalized = sourceCrs?.trim();
  if (!normalized || !tilesUrl.includes('/cog/')) return tilesUrl;
  const cogPath = tilesUrl.slice(tilesUrl.indexOf('/cog/'));
  const queryIndex = cogPath.indexOf('?');
  const path = queryIndex >= 0 ? cogPath.slice(0, queryIndex) : cogPath;
  const query = new URLSearchParams(queryIndex >= 0 ? cogPath.slice(queryIndex + 1) : '');
  query.set('source_crs', normalized.toUpperCase());
  const base = (
    typeof import.meta !== 'undefined'
      ? (import.meta.env?.VITE_RASTER_CRS_URL as string | undefined)
      : undefined
  )?.replace(/\/+$/, '') || '/raster-crs';
  return `${base}${path}?${query.toString()}`;
}
